// src/pages/settings/Profile.jsx
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import CommonFooter from "../../core/common/footer/commonFooter";
import {
  updateUserProfile,
  getUserProfile,
  deleteUser,
} from "../../api/userApi";
import { uploadCatalogImage } from "../../api/productApi";
import { getAllUsersPosReport } from "../../api/transactionReportApi";
import { toast } from "react-toastify";
import PropTypes from "prop-types";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// ---- helpers (same as supplier/biller semantics) -----------------------------
const normalizeKey = (s) => {
  if (!s) return "";
  let v = String(s).trim();
  v = v.replace(/^\/+/, "");
  v = v.replace(/^catelog\//i, "catalog/");
  if (!/^catalog\//i.test(v)) v = `catalog/${v.split("/").pop()}`;
  return v;
};

const toPublicUrl = (v) => {
  if (!v) return "";
  if (/^https?:\/\//i.test(v)) return v;
  const key = normalizeKey(v);
  const base = (
    process.env.REACT_APP_API_IMAGE_BASE_URL ||
    "https://hamilton-shop-images.s3.ap-south-1.amazonaws.com"
  ).replace(/\/+$/, "");
  return `${base}/${key}`;
};

const toLocalizedText = (v) => {
  if (!v) return "";
  if (typeof v === "string") return v;
  if (typeof v === "object") return v.en || v.ar || "";
  return String(v);
};

const broadcastProfileUpdate = ({ firstName, lastName, profileImage }) => {
  try {
    localStorage.setItem("profileFirstName", firstName || "");
    localStorage.setItem("profileLastName", lastName || "");
    localStorage.setItem("profileImage", profileImage || "");
  } catch (_) {
    // ignore storage errors
  }

  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent("profile-updated", {
        detail: { firstName, lastName, profileImage },
      }),
    );
  }
};

const withCacheBust = (url) => {
  if (!url) return "";
  const joiner = url.includes("?") ? "&" : "?";
  return `${url}${joiner}v=${Date.now()}`;
};

const buildUserFileName = (file, first, last, username, email) => {
  const ext =
    (file.type && file.type.split("/")[1]) ||
    file.name.split(".").pop() ||
    "jpg";
  const stamp = new Date()
    .toISOString()
    .replace(/[-:.TZ]/g, "")
    .slice(0, 14);
  const base = (first || last || username || email || "user")
    .toString()
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-_]/g, "");
  // e.g., USER_ai-hamilton_20251112094500.jpg
  return `USER_${base || "user"}_${stamp}.${ext}`;
};
// -----------------------------------------------------------------------------

const formatDate = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "-" : d.toLocaleString();
};

const formatMoney = (value) => {
  const n = Number(value || 0);
  if (!Number.isFinite(n)) return "0.00";
  return n.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatCount = (value) => {
  const n = Number(value || 0);
  return Number.isFinite(n) ? n : 0;
};

const buildAllUsersReportParams = ({
  from,
  to,
  scope = "store",
  userId,
  transactionType,
  orderPaymentType,
} = {}) => {
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), 1);
  const fmt = (d) => d.toISOString().slice(0, 10);

  return {
    scope,
    from: from || fmt(start),
    to: to || fmt(today),
    userId: userId || undefined,
    transactionType: transactionType || undefined,
    orderPaymentType: orderPaymentType || undefined,
  };
};

const toUserLabel = (user = {}) => {
  const first = (user.firstName || "").trim();
  const last = (user.lastName || "").trim();
  const full = `${first} ${last}`.trim();
  return full || user.username || user.email || "Unknown User";
};

const exportAllUsersExcel = (report, params) => {
  const summary = report?.summary || {};
  const unassigned = report?.unassigned || {};
  const rows = Array.isArray(report?.rows) ? report.rows : [];

  const summaryRows = [
    { Metric: "Scope", Value: report?.meta?.scope || params.scope || "-" },
    {
      Metric: "From",
      Value: report?.meta?.from
        ? new Date(report.meta.from).toISOString().slice(0, 10)
        : params.from || "-",
    },
    {
      Metric: "To",
      Value: report?.meta?.to
        ? new Date(report.meta.to).toISOString().slice(0, 10)
        : params.to || "-",
    },
    { Metric: "Transaction Type", Value: report?.meta?.transactionType || "-" },
    {
      Metric: "Order Payment Type",
      Value: report?.meta?.orderPaymentType || "-",
    },
    { Metric: "Total Users", Value: formatCount(summary.totalUsers) },
    {
      Metric: "Total Transactions",
      Value: formatCount(summary.totalTransactions),
    },
    {
      Metric: "Total Transaction Amount",
      Value: Number(summary.totalTransactionAmount || 0),
    },
    {
      Metric: "Total Transaction Items Sold",
      Value: formatCount(summary.totalTransactionItemsSold),
    },
    { Metric: "Total Orders", Value: formatCount(summary.totalOrders) },
    {
      Metric: "Total Order Amount",
      Value: Number(summary.totalOrderAmount || 0),
    },
    {
      Metric: "Total Order Items Sold",
      Value: formatCount(summary.totalOrderItemsSold),
    },
  ];

  const userRows = rows.map((row, idx) => ({
    Sr: idx + 1,
    User: toUserLabel(row?.user),
    Email: row?.user?.email || "-",
    Username: row?.user?.username || "-",
    Role: row?.user?.role || "-",
    Active: row?.user?.active ? "Yes" : "No",
    Transactions: formatCount(row?.transactions?.totalTransactions),
    TransactionAmount: Number(row?.transactions?.totalAmount || 0),
    TransactionItemsSold: formatCount(row?.transactions?.totalItemsSold),
    Orders: formatCount(row?.orders?.totalOrders),
    OrderAmount: Number(row?.orders?.totalAmount || 0),
    OrderItemsSold: formatCount(row?.orders?.totalItemsSold),
    TotalDocuments: formatCount(row?.combined?.totalDocuments),
    TotalAmount: Number(row?.combined?.totalAmount || 0),
    TotalItemsSold: formatCount(row?.combined?.totalItemsSold),
    LastActivityAt: row?.combined?.lastActivityAt
      ? formatDate(row?.combined?.lastActivityAt)
      : "-",
  }));

  const unassignedRows = [
    {
      Section: "Transactions",
      TotalDocuments: formatCount(unassigned?.transactions?.totalTransactions),
      TotalAmount: Number(unassigned?.transactions?.totalAmount || 0),
      TotalItemsSold: formatCount(unassigned?.transactions?.totalItemsSold),
    },
    {
      Section: "Orders",
      TotalDocuments: formatCount(unassigned?.orders?.totalOrders),
      TotalAmount: Number(unassigned?.orders?.totalAmount || 0),
      TotalItemsSold: formatCount(unassigned?.orders?.totalItemsSold),
    },
  ];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(
    wb,
    XLSX.utils.json_to_sheet(summaryRows),
    "Summary",
  );
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(userRows), "Users");
  XLSX.utils.book_append_sheet(
    wb,
    XLSX.utils.json_to_sheet(unassignedRows),
    "Unassigned",
  );
  XLSX.writeFile(wb, `all_users_report_${params.from}_to_${params.to}.xlsx`);
};

const exportAllUsersPdf = (report, params) => {
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const summary = report?.summary || {};
  const unassigned = report?.unassigned || {};
  const rows = Array.isArray(report?.rows) ? report.rows : [];

  doc.setFillColor(236, 170, 64);
  doc.rect(0, 0, pageWidth, 74, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.text("All Users POS Sales Report", 32, 44);

  doc.setTextColor(44, 62, 80);
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  const reportFrom = report?.meta?.from
    ? new Date(report.meta.from).toISOString().slice(0, 10)
    : params.from;
  const reportTo = report?.meta?.to
    ? new Date(report.meta.to).toISOString().slice(0, 10)
    : params.to;
  doc.text(`Scope: ${report?.meta?.scope || params.scope || "-"}`, 32, 98);
  doc.text(`Date Range: ${reportFrom || "-"} to ${reportTo || "-"}`, 200, 98);
  doc.text(`Generated: ${formatDate(new Date())}`, 470, 98);
  doc.text(
    `Filters: tx=${report?.meta?.transactionType || "-"}, order=${report?.meta?.orderPaymentType || "-"}`,
    32,
    114,
  );

  autoTable(doc, {
    startY: 128,
    theme: "grid",
    headStyles: { fillColor: [250, 245, 235], textColor: [60, 60, 60] },
    styles: {
      fontSize: 9,
      cellPadding: 5,
      lineColor: [230, 230, 230],
      lineWidth: 0.4,
    },
    head: [["Summary Metric", "Value"]],
    body: [
      ["Total Users", formatCount(summary.totalUsers)],
      ["Total Transactions", formatCount(summary.totalTransactions)],
      ["Total Transaction Amount", formatMoney(summary.totalTransactionAmount)],
      [
        "Total Transaction Items Sold",
        formatCount(summary.totalTransactionItemsSold),
      ],
      ["Total Orders", formatCount(summary.totalOrders)],
      ["Total Order Amount", formatMoney(summary.totalOrderAmount)],
      ["Total Order Items Sold", formatCount(summary.totalOrderItemsSold)],
    ],
    columnStyles: {
      0: { cellWidth: 240 },
      1: { cellWidth: 140, halign: "right" },
    },
  });

  autoTable(doc, {
    startY: doc.lastAutoTable.finalY + 16,
    theme: "striped",
    headStyles: { fillColor: [52, 73, 94], textColor: [255, 255, 255] },
    styles: { fontSize: 8, cellPadding: 4 },
    head: [
      [
        "User",
        "Role",
        "Active",
        "Tx Count",
        "Tx Amount",
        "Order Count",
        "Order Amount",
        "Total Amount",
        "Last Activity",
      ],
    ],
    body: rows.length
      ? rows.map((row) => [
          `${toUserLabel(row?.user)}\n${row?.user?.email || "-"}`,
          row?.user?.role || "-",
          row?.user?.active ? "Yes" : "No",
          formatCount(row?.transactions?.totalTransactions),
          formatMoney(row?.transactions?.totalAmount),
          formatCount(row?.orders?.totalOrders),
          formatMoney(row?.orders?.totalAmount),
          formatMoney(row?.combined?.totalAmount),
          formatDate(row?.combined?.lastActivityAt),
        ])
      : [
          [
            "No users found in selected range",
            "-",
            "-",
            "-",
            "-",
            "-",
            "-",
            "-",
            "-",
          ],
        ],
    columnStyles: {
      0: { cellWidth: 200 },
      1: { cellWidth: 60 },
      2: { cellWidth: 45, halign: "center" },
      3: { cellWidth: 55, halign: "right" },
      4: { cellWidth: 80, halign: "right" },
      5: { cellWidth: 65, halign: "right" },
      6: { cellWidth: 90, halign: "right" },
      7: { cellWidth: 85, halign: "right" },
      8: { cellWidth: 95 },
    },
  });

  autoTable(doc, {
    startY: doc.lastAutoTable.finalY + 14,
    theme: "grid",
    headStyles: { fillColor: [250, 245, 235], textColor: [60, 60, 60] },
    styles: {
      fontSize: 9,
      cellPadding: 5,
      lineColor: [230, 230, 230],
      lineWidth: 0.4,
    },
    head: [["Unassigned Section", "Documents", "Amount", "Items Sold"]],
    body: [
      [
        "Transactions",
        formatCount(unassigned?.transactions?.totalTransactions),
        formatMoney(unassigned?.transactions?.totalAmount),
        formatCount(unassigned?.transactions?.totalItemsSold),
      ],
      [
        "Orders",
        formatCount(unassigned?.orders?.totalOrders),
        formatMoney(unassigned?.orders?.totalAmount),
        formatCount(unassigned?.orders?.totalItemsSold),
      ],
    ],
    columnStyles: {
      0: { cellWidth: 170 },
      1: { cellWidth: 90, halign: "right" },
      2: { cellWidth: 110, halign: "right" },
      3: { cellWidth: 90, halign: "right" },
    },
  });

  doc.save(`all_users_report_${params.from}_to_${params.to}.pdf`);
};

const Profile = () => {
  const [isPasswordVisible, setPasswordVisible] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [username, setUsername] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  // ✅ image: preview URL + S3 key (send key to backend)
  const [profileImagePreview, setProfileImagePreview] = useState("");
  const [profileImageKey, setProfileImageKey] = useState("");

  // ✅ Social links
  const [socialLinks, setSocialLinks] = useState({
    whatsapp: "",
    instagram: "",
    facebook: "",
    tiktok: "",
    snapchat: "",
  });

  const handleSocialChange = (key, value) => {
    setSocialLinks((prev) => ({ ...prev, [key]: value }));
  };

  const [usage, setUsage] = useState({ limit: 0, used: 0, remaining: 0 });
  const [seatUsage, setSeatUsage] = useState({
    limit: 1,
    used: 0,
    remaining: 1,
  });
  const [storeUsage, setStoreUsage] = useState({
    limit: 1,
    used: 0,
    remaining: 1,
  });
  const [warehouseUsage, setWarehouseUsage] = useState({
    limit: 1,
    used: 0,
    remaining: 1,
  });
  const [showStoresCard, setShowStoresCard] = useState(false);
  const [reportLoading, setReportLoading] = useState("");
  const Navigate = useNavigate();

  // ⏬ Fetch user data on mount
  useEffect(() => {
    const fetchProfile = async () => {
      const result = await getUserProfile();
      if (!result.success) {
        alert(window.translate("Failed to load user profile"));
        return;
      }
      const data = result.data || {};

      const first = toLocalizedText(data.firstName);
      const last = toLocalizedText(data.lastName);
      const user = toLocalizedText(data.username);
      setFirstName(first);
      setLastName(last);
      setUsername(user);
      setPhone(data.phone || "");
      setEmail(data.email || "");
      setSocialLinks({
        whatsapp: data?.socialLinks?.whatsapp || "",
        instagram: data?.socialLinks?.instagram || "",
        facebook: data?.socialLinks?.facebook || "",
        tiktok: data?.socialLinks?.tiktok || "",
        snapchat: data?.socialLinks?.snapchat || "",
      });

      // 🔄 Normalize existing profile image -> preview + key
      const existing =
        data.profileImageUrl || data.profileImage || data.profileImageKey || "";
      const existingKey = data.profileImage || data.profileImageKey || existing;
      const previewUrl = existing ? toPublicUrl(existing) : "";
      setProfileImageKey(existingKey ? normalizeKey(existingKey) : "");
      setProfileImagePreview(previewUrl);
      broadcastProfileUpdate({
        firstName: first,
        lastName: last,
        profileImage: previewUrl,
      });

      // ---- usage blocks
      const u = data.usage || {};
      setUsage({
        limit: u.product?.limit ?? 0,
        used: u.product?.used ?? 0,
        remaining:
          u.product?.limit === 0
            ? null
            : Math.max((u.product?.limit || 0) - (u.product?.used || 0), 0),
      });
      setSeatUsage({
        limit: u.users?.limit ?? 1,
        used: u.users?.used ?? 0,
        remaining:
          u.users?.limit === 0
            ? null
            : Math.max((u.users?.limit || 0) - (u.users?.used || 0), 0),
      });

      const hasStores = !!u.stores;
      setShowStoresCard(hasStores);
      if (hasStores) {
        setStoreUsage({
          limit: u.stores?.limit ?? 1,
          used: u.stores?.used ?? 0,
          remaining:
            u.stores?.limit === 0
              ? null
              : Math.max((u.stores?.limit || 0) - (u.stores?.used || 0), 0),
        });
      } else {
        setStoreUsage({ limit: 0, used: 0, remaining: 0 });
      }

      setWarehouseUsage({
        limit: u.warehouses?.limit ?? 1,
        used: u.warehouses?.used ?? 0,
        remaining:
          u.warehouses?.limit === 0
            ? null
            : Math.max(
                (u.warehouses?.limit || 0) - (u.warehouses?.used || 0),
                0,
              ),
      });
    };

    fetchProfile();
  }, []);

  const handleSaveProfile = async () => {
    const safeFirstName = String(firstName || "").trim();
    const safeLastName = String(lastName || "").trim();
    const safeUsername = String(username || "").trim();
    const resolvedProfileKey =
      profileImageKey || normalizeKey(profileImagePreview || "");

    const profileData = {
      firstName: safeFirstName,
      lastName: safeLastName,
      username: safeUsername,
      phone,
      email,
      password,
      // ✅ send S3 key only; fallback to normalized preview if user never changed it
      profileImage: resolvedProfileKey,
      socialLinks,
    };

    const result = await updateUserProfile(profileData);
    if (result.success) {
      const savedImageSource =
        result?.data?.profileImageUrl ||
        result?.data?.profileImage ||
        resolvedProfileKey;
      broadcastProfileUpdate({
        firstName: safeFirstName,
        lastName: safeLastName,
        profileImage: savedImageSource
          ? withCacheBust(toPublicUrl(savedImageSource))
          : "",
      });
      alert(window.translate("Profile updated successfully"));
      Navigate("/index");
    } else {
      alert(window.translate(`Profile Update failed: ${result.error}`));
    }
  };

  // ✅ Upload to S3 immediately on selection (like supplier)
  // ✅ Upload to S3 immediately on selection (like supplier) — fixed to await DataURL
  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // (optional) max 2MB guard
    if (file.size > 2 * 1024 * 1024) {
      toast.error(window.translate("Image too large (max 2 MB)"));
      return;
    }

    try {
      // 1) Read file as data URL (await!)
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      // Show immediate preview
      setProfileImagePreview(dataUrl);

      // 2) Build a good filename
      const desired = buildUserFileName(
        file,
        firstName,
        lastName,
        username,
        email,
      );

      // 3) Call your uploader with the **correct shape**
      // If your helper expects positional args:
      // const { key } = await uploadCatalogImage(dataUrl, desired);

      // If your helper expects an object (most common in these backends):
      const { key } = await uploadCatalogImage(dataUrl, desired);

      // 4) Update state with normalized key + public URL
      const k = normalizeKey(key);
      setProfileImageKey(k);
      setProfileImagePreview(toPublicUrl(k));
    } catch (err) {
      console.error(err);
      toast.error(window.translate("Image upload failed"));
      setProfileImagePreview(
        profileImageKey ? toPublicUrl(profileImageKey) : "",
      );
    }
  };

  const togglePasswordVisibility = () => {
    setPasswordVisible((prevState) => !prevState);
  };

  const handleDeleteAccount = async () => {
    if (
      window.confirm(
        window.translate(
          "Are you sure you want to delete your account? This action cannot be undone.",
        ),
      )
    ) {
      const result = await deleteUser();
      if (result.success) {
        alert(window.translate("Account deleted successfully"));
        localStorage.removeItem("token");
        localStorage.removeItem("authToken");
        window.location.href = "/login";
      } else {
        alert(window.translate(`Error in delete profile: ${result.error}`));
      }
    }
  };

  const handleDownloadAllUsersReport = async (format) => {
    if (reportLoading) return;

    setReportLoading(format);
    try {
      const params = buildAllUsersReportParams({ scope: "store" });
      const report = await getAllUsersPosReport(params);

      if (!report?.success) {
        throw new Error(report?.message || "Failed to fetch report");
      }

      if (format === "excel") {
        exportAllUsersExcel(report, params);
      } else {
        exportAllUsersPdf(report, params);
      }
    } catch (err) {
      console.error("All users report export failed:", err);
      alert(
        err?.response?.data?.message ||
          err?.message ||
          window.translate("Failed to download. Please try again."),
      );
    } finally {
      setReportLoading("");
    }
  };

  const UsageCard = ({ iconClass, title, limit, used }) => {
    const percent =
      limit === 0
        ? 0
        : Math.min(Math.round((used / Math.max(limit, 1)) * 100), 100);

    UsageCard.propTypes = {
      iconClass: PropTypes.string.isRequired,
      title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
      limit: PropTypes.number.isRequired,
      used: PropTypes.number.isRequired,
    };

    return (
      <div className="card shadow-sm border-0 h-100">
        <div className="card-body">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <div className="d-flex align-items-center">
              <span
                className="rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: 36, height: 36, background: "#FFF5E6" }}
              >
                <i className={`ti ${iconClass} text-warning fs-6`} />
              </span>
              <div>
                <div className="fw-semibold">{title}</div>
                <div className="text-muted small">
                  {window.translate("Track your inventory capacity")}
                </div>
              </div>
            </div>
            <span className="badge rounded-pill bg-warning-subtle text-warning border">
              {limit === 0
                ? window.translate("Unlimited")
                : `${limit} ${window.translate("Limit")}`}
            </span>
          </div>

          {limit === 0 ? (
            <div className="text-muted small">
              {window.translate("No limit applied to this account.")}
            </div>
          ) : (
            <>
              <div className="progress" style={{ height: 10 }}>
                <div
                  className="progress-bar bg-warning"
                  role="progressbar"
                  style={{ width: `${percent}%` }}
                  aria-valuenow={used}
                  aria-valuemin={0}
                  aria-valuemax={limit}
                />
              </div>
              <div className="d-flex justify-content-between mt-2 small">
                <span className="text-muted">
                  {window.translate("Used")}:{" "}
                  <strong className="text-dark">{used}</strong>
                </span>
                <span className="text-muted">
                  {window.translate("Remaining")}:{" "}
                  <strong className="text-dark">
                    {limit === 0
                      ? "—"
                      : Math.max((limit || 0) - (used || 0), 0)}
                  </strong>
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Header */}
        <div className="page-header mb-3">
          <div className="page-title">
            <h4 className="mb-1">{window.translate("Profile")}</h4>
            <h6 className="text-muted m-0">
              {window.translate("User Profile")}
            </h6>
          </div>
          <div className="d-flex justify-content-end mb-3 gap-2">
            <Link to="/settings/users" className="btn btn-primary">
              <i className="ti ti-users-plus me-2 ms-2" />
              {window.translate("Manage Users & Permissions")}
            </Link>
            <div className="dropdown">
              <button
                className="btn btn-outline-primary dropdown-toggle"
                type="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                disabled={!!reportLoading}
              >
                <i className="ti ti-download me-1 ms-2" />
                {reportLoading
                  ? window.translate("Preparing Report")
                  : window.translate("Download All Users Report")}
              </button>
              <ul className="dropdown-menu dropdown-menu-end">
                <li>
                  <button
                    className="dropdown-item"
                    type="button"
                    disabled={!!reportLoading}
                    onClick={() => handleDownloadAllUsersReport("pdf")}
                  >
                    <i className="ti ti-file-type-pdf me-2 text-danger" />
                    {window.translate("Download PDF")}
                  </button>
                </li>
                <li>
                  <button
                    className="dropdown-item"
                    type="button"
                    disabled={!!reportLoading}
                    onClick={() => handleDownloadAllUsersReport("excel")}
                  >
                    <i className="ti ti-file-spreadsheet me-2 text-success" />
                    {window.translate("Download Excel")}
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="ms-auto d-none d-md-flex align-items-center gap-2 mb-3">
          <span className="badge bg-light text-dark border">
            {window.translate("ID")}: <strong className="ms-1">{email}</strong>
          </span>
        </div>

        {/* Usage + Quick identity */}
        <div className="row g-3 mb-4">
          {/* Product Usage Card */}
          <div className="col-12 col-lg-6">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <div className="d-flex align-items-center">
                    <span
                      className="rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                      style={{ width: 36, height: 36, background: "#FFF5E6" }}
                    >
                      <i className="ti ti-package text-warning fs-6" />
                    </span>
                    <div>
                      <div className="fw-semibold">
                        {window.translate("Product Usage")}
                      </div>
                      <div className="text-muted small">
                        {window.translate("Track your inventory capacity")}
                      </div>
                    </div>
                  </div>
                  <span className="badge rounded-pill bg-warning-subtle text-warning border">
                    {usage.limit === 0 ? "Unlimited" : `${usage.limit} limit`}
                  </span>
                </div>

                {usage.limit === 0 ? (
                  <div className="text-muted small">
                    {window.translate("No limit applied to this account.")}
                  </div>
                ) : (
                  <>
                    <div className="progress" style={{ height: 10 }}>
                      <div
                        className="progress-bar bg-warning"
                        role="progressbar"
                        style={{
                          width: `${Math.min(
                            Math.round(
                              (usage.used / Math.max(usage.limit, 1)) * 100,
                            ),
                            100,
                          )}%`,
                        }}
                        aria-valuenow={usage.used}
                        aria-valuemin={0}
                        aria-valuemax={usage.limit}
                      />
                    </div>
                    <div className="d-flex justify-content-between mt-2 small">
                      <span className="text-muted">
                        {window.translate("Used")}:{" "}
                        <strong className="text-dark">{usage.used}</strong>
                      </span>
                      <span className="text-muted">
                        {window.translate("Remaining")}:{" "}
                        <strong className="text-dark">
                          {usage.limit === 0 ? "—" : usage.remaining}
                        </strong>
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Identity / avatar */}
          <div className="col-12 col-lg-6">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body d-flex align-items-center">
                <div className="me-3">
                  <div
                    className="rounded-circle overflow-hidden border"
                    style={{ width: 64, height: 64, background: "#f3f6f8" }}
                  >
                    {profileImagePreview ? (
                      <img
                        src={profileImagePreview}
                        alt="Avatar"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <div className="w-100 h-100 d-flex align-items-center justify-content-center text-muted">
                        <i className="ti ti-user fs-3" />
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex-grow-1">
                  <div className="fw-semibold">
                    {firstName} {lastName}{" "}
                    <span className="text-muted">@{username}</span>
                  </div>
                  <div className="text-muted small">{email}</div>
                  <div className="mt-2">
                    <label className="btn btn-sm btn-outline-secondary shadow-none mb-0">
                      {window.translate("Change Avatar")}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        hidden
                      />
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* NEW: Seats / Stores / Warehouses usage */}
        <div className="row g-3 mb-4">
          <div className="col-12 col-lg-4">
            <UsageCard
              iconClass="ti-users"
              title={window.translate("User Usage")}
              limit={seatUsage.limit}
              used={seatUsage.used}
            />
          </div>
          {showStoresCard && (
            <div className="col-12 col-lg-4">
              <UsageCard
                iconClass="ti-building-store"
                title={window.translate("Store Usage")}
                limit={storeUsage.limit}
                used={storeUsage.used}
              />
            </div>
          )}
          <div className="col-12 col-lg-4">
            <UsageCard
              iconClass="ti-building-warehouse"
              title={window.translate("Warehouse Usage")}
              limit={warehouseUsage.limit}
              used={warehouseUsage.used}
            />
          </div>
        </div>

        {/* Profile form */}
        <div className="card border-0 shadow-sm">
          <div className="card-header bg-white">
            <h5 className="mb-0 d-flex align-items-center">
              <i className="ti ti-user-circle text-primary me-2" />
              {window.translate("Basic Information")}
            </h5>
          </div>

          <div className="card-body">
            <div className="row g-3">
              <div className="col-lg-6">
                <label className="form-label">
                  {window.translate("First Name")}
                  <span className="text-danger ms-1">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </div>

              <div className="col-lg-6">
                <label className="form-label">
                  {window.translate("Last Name")}
                  <span className="text-danger ms-1">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </div>

              <div className="col-lg-6">
                <label className="form-label">
                  {window.translate("Email")}
                  <span className="text-danger ms-1">*</span>
                </label>
                <input
                  type="email"
                  className="form-control"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <div className="col-lg-6">
                <label className="form-label">
                  {window.translate("Phone Number")}
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>

              <div className="col-lg-6">
                <label className="form-label">
                  {window.translate("User Name")}
                  <span className="text-danger ms-1">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                />
              </div>

              <div className="col-lg-6">
                <label className="form-label">
                  {window.translate("Password")}
                </label>
                <div className="input-group">
                  <input
                    type={isPasswordVisible ? "text" : "password"}
                    className="form-control"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={togglePasswordVisibility}
                    title={isPasswordVisible ? "Hide" : "Show"}
                  >
                    <i
                      className={`ti ${
                        isPasswordVisible ? "ti-eye-off" : "ti-eye"
                      }`}
                    />
                  </button>
                </div>

                {/* ✅ Social Links */}
                <hr className="my-4" />
                <h5 className="mb-3 d-flex align-items-center">
                  <i className="ti ti-brand-linkedin text-primary me-2" />
                  {window.translate("Social Links")}
                </h5>

                <div className="row g-3">
                  <div className="col-lg-6">
                    <label className="form-label">
                      {window.translate("WhatsApp")}
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="+96512345678 or https://wa.me/96512345678"
                      value={socialLinks.whatsapp}
                      onChange={(e) =>
                        handleSocialChange("whatsapp", e.target.value)
                      }
                    />
                  </div>

                  <div className="col-lg-6">
                    <label className="form-label">
                      {window.translate("Instagram")}
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="https://instagram.com/yourpage"
                      value={socialLinks.instagram}
                      onChange={(e) =>
                        handleSocialChange("instagram", e.target.value)
                      }
                    />
                  </div>

                  <div className="col-lg-6">
                    <label className="form-label">
                      {window.translate("Facebook")}
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="https://facebook.com/yourpage"
                      value={socialLinks.facebook}
                      onChange={(e) =>
                        handleSocialChange("facebook", e.target.value)
                      }
                    />
                  </div>

                  <div className="col-lg-6">
                    <label className="form-label">
                      {window.translate("TikTok")}
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="https://tiktok.com/@yourpage"
                      value={socialLinks.tiktok}
                      onChange={(e) =>
                        handleSocialChange("tiktok", e.target.value)
                      }
                    />
                  </div>

                  <div className="col-lg-6">
                    <label className="form-label">
                      {window.translate("Snapchat")}
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="https://snapchat.com/add/yourpage"
                      value={socialLinks.snapchat}
                      onChange={(e) =>
                        handleSocialChange("snapchat", e.target.value)
                      }
                    />
                  </div>
                </div>

                <div className="form-text"></div>
              </div>
            </div>
          </div>

          <div className="card-footer bg-white d-flex justify-content-end gap-2">
            <Link to="/index" className="btn btn-light border shadow-none">
              {window.translate("Cancel")}
            </Link>
            <button
              className="btn btn-primary shadow-none"
              onClick={handleSaveProfile}
            >
              {window.translate("Save Changes")}
            </button>
            <button
              className="btn btn-outline-danger shadow-none"
              onClick={handleDeleteAccount}
            >
              {window.translate("Delete Account")}
            </button>
          </div>
        </div>
      </div>

      <CommonFooter />
    </div>
  );
};

export default Profile;
